/**
 * Answer design utilities — implements ANSWER_DESIGN.md in code.
 */

export type QuestionWithAnswers = {
  correctAnswer?: string;
  correctAnswers?: string[];
  placeholderHint?: string;
  baseForm?: string;
  text?: string;
};

/** All accepted answer strings for a question. */
export function getAcceptedAnswers(question: QuestionWithAnswers): string[] {
  if (question.correctAnswers?.length) {
    return question.correctAnswers;
  }
  if (question.correctAnswer != null && question.correctAnswer !== '') {
    return [question.correctAnswer];
  }
  return [];
}

const META_PLACEHOLDER_HINTS = new Set([
  'possessive',
  'object pronoun',
  'possibility',
  'negative deduction',
  'reflexive',
  'reciprocal',
]);

/** Answers where placeholder = answer gives away word choice (not form). */
const WORD_CHOICE_ANSWERS = new Set([
  'themselves', 'himself', 'herself', 'itself', 'each other',
  'those', 'theirs', 'yours', 'ours', 'mine', 'whose',
  'who', 'whom', 'which', 'that',
  'me', 'him', 'her', 'them', 'us', 'you', 'i', 'he', 'she', 'we', 'they',
  'than', 'however', 'therefore', 'although', 'otherwise',
  'unnoticed', 'insightful', 'depth',
]);

/**
 * True when the placeholder is the full answer and tests vocabulary/choice, not inflection.
 * Allows e.g. hint "go" + answer "go" (present simple) or "in" + "in" (preposition drill).
 */
export function isGiveawayPlaceholder(hint: string, accepted: string[]): boolean {
  if (accepted.length !== 1) return false;
  const answer = accepted[0].toLowerCase().trim();
  const h = hint.toLowerCase().trim();
  if (h !== answer) return false;
  if (answer.includes(' ')) return true;
  return WORD_CHOICE_ANSWERS.has(answer);
}

function isForbiddenPlaceholderHint(hint: string, accepted: string[]): boolean {
  const normalized = hint.trim().toLowerCase();
  if (!normalized) return true;
  if (hint.includes('/')) return true;
  if (META_PLACEHOLDER_HINTS.has(normalized)) return true;
  if (isGiveawayPlaceholder(hint, accepted)) return true;
  return false;
}

/**
 * Placeholder for fill-in inputs per ANSWER_DESIGN.md §4.
 */
export function getPlaceholderHint(question: QuestionWithAnswers): string {
  const accepted = getAcceptedAnswers(question);

  if (accepted.length > 1) {
    return '?';
  }

  if (question.placeholderHint) {
    const hint = String(question.placeholderHint).trim();
    if (!isForbiddenPlaceholderHint(hint, accepted)) {
      return hint;
    }
    return '?';
  }

  if (question.baseForm) {
    return question.baseForm;
  }

  const text = question.text ?? '';
  const match = text.match(/\(([^)]+)\)/);
  if (match) {
    return match[1];
  }

  return '?';
}

/** Authoring-time check: hint lists options not all accepted. */
export function getHintAnswerMismatches(
  placeholderHint: string | undefined,
  accepted: string[]
): string[] {
  if (!placeholderHint?.includes('/')) return [];
  if (accepted.length <= 1) return [];

  const alts = placeholderHint
    .split('/')
    .map((s) => s.trim().toLowerCase())
    .filter(Boolean);

  const normalized = accepted.map((a) => a.toLowerCase().trim());
  const hasExactOption = alts.some((a) => normalized.includes(a));
  if (!hasExactOption) return [];

  return alts.filter((a) => !normalized.includes(a));
}
